import { StatBlock } from "@/pages/admin/AdminShared";
import DeploySafetyCard from "@/pages/admin/DeploySafetyCard";
import FeatureFlagsCard from "@/pages/admin/FeatureFlagsCard";
import AudioDiagnosticsPanel from "@/pages/admin/AudioDiagnosticsPanel";
import PermissionFlowCard from "@/pages/admin/PermissionFlowCard";

function LauncherCard({ s, onOpen }) {
  const { v, label, Icon, color, bullets } = s;
  return (
    <button
      type="button"
      data-testid={`admin-launcher-${v}`}
      onClick={() => onOpen(v)}
      className="group text-left card-surface p-4 border border-white/5 hover:border-rose-500/50 hover:bg-white/[0.03] transition-colors flex gap-3"
    >
      <span className={`mt-0.5 shrink-0 w-9 h-9 grid place-items-center bg-black/40 border border-white/10 ${color}`}><Icon size={17} /></span>
      <div className="min-w-0">
        <div className="font-semibold text-sm text-white group-hover:text-rose-200">{label}</div>
        <ul className="mt-1.5 space-y-0.5 text-[11px] text-zinc-400">
          {bullets.map((b) => <li key={b} className="flex gap-1.5"><span className="text-zinc-600">•</span><span>{b}</span></li>)}
        </ul>
      </div>
    </button>
  );
}

function TrafficCard({ analytics }) {
  return (
    <div className="card-surface p-5" data-testid="analytics-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-rose-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 pulse-dot" /> Visitors
          </div>
          <h3 className="font-display text-xl mt-1">Site traffic</h3>
        </div>
        <div className="text-right">
          <div className="text-3xl font-display text-emerald-400" data-testid="online-now-count">{analytics.online_now}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">online now</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[{ key: "day", label: "Last 24h" }, { key: "week", label: "Last 7 days" }, { key: "month", label: "Last 30 days" }].map((w) => (
          <div key={w.key} className="bg-black/30 border border-white/5 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{w.label}</div>
            <div className="text-xl font-display" data-testid={`views-${w.key}`}>{analytics.views[w.key].toLocaleString()}</div>
            <div className="text-[11px] text-zinc-500">{analytics.unique_visitors[w.key].toLocaleString()} unique</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Top pages (7d)</div>
      <div className="space-y-1" data-testid="analytics-top-paths">
        {(analytics.top_paths || []).slice(0, 8).map((p) => (
          <div key={p.path} className="flex items-center justify-between text-xs bg-black/20 px-3 py-1.5 border-l-2 border-white/10">
            <span className="text-zinc-300 truncate mr-3 font-mono">{p.path}</span>
            <span className="text-amber-400">{p.count.toLocaleString()}</span>
          </div>
        ))}
        {(!analytics.top_paths || analytics.top_paths.length === 0) && (
          <div className="text-xs text-zinc-500 py-2">No traffic yet — kick things off by sharing the /live-now link.</div>
        )}
      </div>
    </div>
  );
}

export default function DashboardTab({ stats, analytics, sections, onOpen, user }) {
  const launchers = sections.filter((s) => s.launcher !== false);
  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatBlock label="Users" value={stats.users.total} hint={`${stats.users.creators} creators · ${stats.users.banned} banned`} />
          <StatBlock label="Posts" value={stats.posts.total} hint={`+${stats.posts.last_24h} in 24h`} />
          <StatBlock label="Live now" value={stats.live.now} />
          <StatBlock label="Coins gifted" value={stats.gifts.coins_sent} hint={`${stats.gifts.count} gifts`} />
          <StatBlock label="Open reports" value={stats.reports?.open ?? 0} hint="Need review" />
        </div>
      )}
      {analytics && <TrafficCard analytics={analytics} />}
      <div className="grid xl:grid-cols-[minmax(0,1fr)_280px] gap-6 items-start">
        <div className="grid sm:grid-cols-2 gap-3" data-testid="admin-launcher-grid">
          {launchers.map((s) => <LauncherCard key={s.v} s={s} onOpen={onOpen} />)}
        </div>
        <aside className="space-y-4" data-testid="admin-dashboard-side">
          <DeploySafetyCard />
          <FeatureFlagsCard user={user} />
          <div className="card-surface p-4"><AudioDiagnosticsPanel /></div>
          <PermissionFlowCard user={user} />
        </aside>
      </div>
    </div>
  );
}
